import type {
  EnrichedWakeupCall,
  WakeupCall,
  WakeupDisplayStatus,
} from '../types/wakeup'
import { extensionUuidKey } from './extensionUuid'

interface ExtensionData {
  extension_uuid: string
  number: string
  name: string
}

/**
 * Compute display_status from wake-up call fields.
 * Logic matches the backend WakeUpCall.get_display_status() method.
 */
export const computeDisplayStatus = (wc: WakeupCall): WakeupDisplayStatus => {
  if (wc.deleted_at != null) return 'canceled'
  if (wc.done && wc.status === 1) return 'cleared'
  if (!wc.done && wc.status === 1) return 'tried'
  return 'scheduled'
}

/**
 * Enrich a wake-up call with extension data (room_number, room_name, display_status).
 * Used when the API returns slim responses (proxy mode).
 */
export const enrichWakeupCall = (
  wc: WakeupCall,
  extensionMap: Map<string, ExtensionData>,
): EnrichedWakeupCall => {
  if (
    wc.room_number !== undefined &&
    wc.room_name !== undefined &&
    wc.display_status !== undefined
  ) {
    return wc as EnrichedWakeupCall
  }

  const ext = extensionMap.get(extensionUuidKey(wc.extension_uuid))

  return {
    ...wc,
    room_number: wc.room_number ?? ext?.number ?? '',
    room_name: wc.room_name ?? ext?.name ?? '',
    display_status: wc.display_status ?? computeDisplayStatus(wc),
  }
}

/**
 * Enrich a list of wake-up calls with extension data.
 */
export const enrichWakeupCalls = (
  calls: WakeupCall[],
  extensionMap: Map<string, ExtensionData>,
): EnrichedWakeupCall[] => {
  return calls.map((wc) => enrichWakeupCall(wc, extensionMap))
}

/**
 * Build an extension map from extension-lines data for quick lookup.
 */
export const buildExtensionMap = (
  extensions: Array<{ extension_uuid: string; number: string; name: string }>,
): Map<string, ExtensionData> => {
  const map = new Map<string, ExtensionData>()
  for (const ext of extensions) {
    map.set(extensionUuidKey(ext.extension_uuid), ext)
  }
  return map
}
